import React, { useRef, useEffect, useState, useCallback } from 'react';
import LogoBlanc from '../components/LogoBlanc';
import NavigationMenu from '../components/NavigationMenu';
import './Phaino.css';

const SECTIONS = 5;

// ── Composant lecteur audio ──────────────────────────────────
const AudioPlayer = ({ label, src, volume = 0.8 }) => {
  const [playing,  setPlaying]  = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const aRef = useRef(null);

  const toggle = () => {
    if (!aRef.current) {
      aRef.current = new Audio(src);
      aRef.current.ontimeupdate   = () => setProgress(aRef.current.currentTime / (aRef.current.duration || 1));
      aRef.current.onloadedmetadata = () => setDuration(aRef.current.duration);
      aRef.current.volume = volume;
      aRef.current.onended        = () => setPlaying(false);
    }
    if (playing) { aRef.current.pause(); setPlaying(false); }
    else         { aRef.current.play().catch(()=>{}); setPlaying(true); }
  };

  const seek = (e) => {
    if (!aRef.current) return;
    const r = e.currentTarget.getBoundingClientRect();
    aRef.current.currentTime = ((e.clientX - r.left) / r.width) * (aRef.current.duration || 0);
  };

  useEffect(() => () => aRef.current?.pause(), []);

  const fmt = (s) =>
    `${Math.floor(s/60).toString().padStart(2,'0')}:${Math.floor(s%60).toString().padStart(2,'0')}`;

  return (
    <div className="ph-aplayer">
      <div className="ph-aplayer__label">{label}</div>
      <div className="ph-aplayer__controls">
        <button className="ph-aplayer__btn" onClick={toggle}>
          {playing
            ? <svg viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="4" width="4" height="16"/><rect x="15" y="4" width="4" height="16"/></svg>
            : <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
          }
        </button>
        <div className="ph-aplayer__timeline" onClick={seek}>
          <div className="ph-aplayer__progress" style={{ width: `${progress * 100}%` }} />
        </div>
        <span className="ph-aplayer__time">{fmt(progress * duration)}</span>
      </div>
    </div>
  );
};

const Phaino = () => {
  const containerRef   = useRef(null);
  const isScrollingRef = useRef(false);
  const scrollTimeout  = useRef(null);
  const audioRefs      = useRef({});
  const [activeSection, setActiveSection] = useState(0);
  const [audioPlaying,  setAudioPlaying]  = useState({});
  const videoRef       = useRef(null);
  const screenshotRef  = useRef(null);
  const schemaRef      = useRef(null);
  const [schemaH, setSchemaH] = useState(null);
  const videoProgressRef = useRef(null);
  const videoTimeRef     = useRef(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoPaused,   setVideoPaused]   = useState(false);

  // ── Scroll wheel ancré ───────────────────────────────────────
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container || isScrollingRef.current) return;
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => {
      isScrollingRef.current = true;
      const W   = container.offsetWidth;
      const cur = container.scrollLeft;
      const dir = e.deltaY > 0 ? 1 : -1;
      const anchors = Array.from({ length: SECTIONS }, (_, i) => i * W);
      let target;
      if (dir > 0) target = anchors.find(a => a > cur + 10) ?? anchors[SECTIONS - 1];
      else         target = [...anchors].reverse().find(a => a < cur - 10) ?? 0;
      target = Math.max(0, Math.min(container.scrollWidth - container.clientWidth, target));
      setActiveSection(Math.round(target / W));
      const t0 = performance.now(), s0 = container.scrollLeft, dist = target - s0, dur = 280;
      const anim = (now) => {
        const p  = Math.min((now - t0) / dur, 1);
        const ep = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        container.scrollLeft = s0 + dist * ep;
        if (p < 1) requestAnimationFrame(anim); else isScrollingRef.current = false;
      };
      requestAnimationFrame(anim);
    }, 3);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
  
  return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const navigateTo = useCallback((i) => {
    const el = containerRef.current;
    if (!el) return;
    const target = i * el.offsetWidth;
    const s0 = el.scrollLeft, dist = target - s0;
    if (dist === 0) return;
    setActiveSection(i);
    // Bloquer scroll-behavior natif pendant l'animation
    el.style.overflowX = 'hidden';
    const t0 = performance.now(), dur = 280;
    const anim = (now) => {
      const p  = Math.min((now - t0) / dur, 1);
      const ep = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      el.scrollLeft = s0 + dist * ep;
      if (p < 1) {
        requestAnimationFrame(anim);
      } else {
        el.style.overflowX = 'auto';
      }
    };
    requestAnimationFrame(anim);
  }, []);

  // ── Audio ─────────────────────────────────────────────────────
  const toggleAudio = useCallback((key, src) => {
    if (audioRefs.current[key]) {
      audioRefs.current[key].pause();
      audioRefs.current[key] = null;
      setAudioPlaying(p => ({ ...p, [key]: false }));
    } else {
      const a = new Audio(src);
      a.loop = true; a.volume = 0;
      a.play().catch(() => {});
      audioRefs.current[key] = a;
      let v = 0;
      const fade = setInterval(() => {
        v = Math.min(v + 0.03, 0.8);
        if (audioRefs.current[key]) audioRefs.current[key].volume = v;
        if (v >= 0.8) clearInterval(fade);
      }, 50);
      setAudioPlaying(p => ({ ...p, [key]: true }));
    }
  }, []);

  useEffect(() => () => Object.values(audioRefs.current).forEach(a => a?.pause()), []);

  const AUDIOS = [
    { key: 'a1', src: '/media/PHAINO/SamplesTests-20260505T084131Z-3-001/SamplesTests/ShrutiBoxBoucleFx.wav',   label: 'Shruti Box — Boucle Fx',   volume: 0.6 },
    { key: 'a2', src: '/media/PHAINO/SamplesTests-20260505T084131Z-3-001/SamplesTests/BoucleSazAvecEffets.wav', label: 'Saz — Boucle avec effets', volume: 0.95 },
  ];

  const GALLERY = [10, 20, 30, 40, 60, 70, 80];


  return (
    <>
      <NavigationMenu />
      <LogoBlanc />
      <h1 style={{
        position: 'fixed', top: '2.7rem', right: '60px',
        fontFamily: "'Source Sans 3', sans-serif",
        fontSize: 'clamp(2.5rem, 4vw, 3rem)', fontWeight: 300,
        color: 'rgba(255,255,255,0.55)', letterSpacing: '0.2rem',
        textAlign: 'right', textTransform: 'uppercase',
        margin: 0, pointerEvents: 'none', zIndex: 1000, lineHeight: 1,
      }}>Phaínō</h1>

      {/* ── Zone de défilement calquée sur MouseZoneBackground ── */}
      <div className="ph-outer">
        <div className="ph-container" ref={containerRef}>

          {/* ══ PAGE 1 ══ */}
          <section className="ph-section">
            <div className="ph-grid-1">

              {/* Colonne gauche — image */}
              <div className="ph-grid-1__img">
                <img src="/media/PHAINO/OeilSoleil81.jpg" alt="Phaínō" />
              </div>

              {/* Colonne droite — texte */}
              <div className="ph-grid-1__txt">
                <p className="ph-title">Phaínō — une étude du phénomène de parhélie</p>
                <p className="ph-text">Installation audiovisuelle immersive</p>
                <p className="ph-text">Performance en temps réel</p>
                <p className="ph-text">Composition interactive</p>
                <p className="ph-body">
                  Phaínō est une installation immersive et performative inspirée du parhélie,
                  phénomène optique rare où le soleil se dédouble en halos lumineux. Pensé comme
                  une traversée perceptive, le projet déploie un environnement audiovisuel spatialisé
                  activé en temps réel par des performeur·euses.
                  <br /><br />
                  Le public y évolue librement et agit subtilement sur les états visuels et sonores via des
                  interfaces tactiles, devenant partie prenante du phénomène.
                  <br /><br />
                  À la croisée de l'installation et du live A/V, Phaínō propose une expérience collective où
                  narration sensorielle, illusion et altération des repères se conjuguent pour faire émerger une
                  relation renouvelée entre perception, technologie et vivant.
                </p>
              </div>

            </div>
          </section>

          {/* ══ PAGE 2 — Grid 75% | 25% ══ */}
          <section className="ph-section">
            <div className="ph-grid-2">

              {/* Col gauche 75% — Vidéo + contrôles discrets */}
              <div className="ph-grid-2__video">
                <video
                  ref={videoRef}
                  muted playsInline
                  className="ph-video2"
                  onTimeUpdate={() => {
                    const v = videoRef.current;
                    if (!v) return;
                    const p = v.currentTime / (v.duration || 1);
                    // Mise à jour DOM directe — pas de re-render React
                    if (videoProgressRef.current)
                      videoProgressRef.current.style.width = `${p * 100}%`;
                    if (videoTimeRef.current) {
                      const s = v.currentTime;
                      videoTimeRef.current.textContent =
                        `${Math.floor(s/60).toString().padStart(2,'0')}:${Math.floor(s%60).toString().padStart(2,'0')}`;
                    }
                  }}
                  onLoadedMetadata={() => {
                    const v = videoRef.current;
                    if (!v) return;
                    setVideoDuration(v.duration); // gardé pour seek
                    v.play().then(() => setVideoPaused(false)).catch(() => {});
                  }}
                >
                  {/* Remplacer OeilSoleil2.mov par OeilSoleil2.mp4 après conversion FFmpeg */}
                  <source src="/media/PHAINO/PhainoBeta.mp4" type="video/mp4" />
                </video>
                {/* Contrôles vidéo discrets */}
                <div className="ph-video-controls">
                  {/* Timeline */}
                  <div
                    className="ph-video-timeline"
                    onClick={e => {
                      const v = videoRef.current;
                      if (!v) return;
                      const r = e.currentTarget.getBoundingClientRect();
                      v.currentTime = ((e.clientX - r.left) / r.width) * (v.duration || 0);
                    }}
                  >
                    <div className="ph-video-progress" ref={videoProgressRef} />
                  </div>
                  {/* Boutons */}
                  <div className="ph-video-btns">
                    <button
                      className="ph-video-btn"
                      onClick={() => {
                        const v = videoRef.current;
                        if (!v) return;
                        if (v.paused) {
                          v.play().then(() => setVideoPaused(false)).catch(() => {});
                        } else {
                          v.pause();
                          setVideoPaused(true);
                        }
                      }}
                    >
                      {videoPaused
                        ? <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
                        : <svg viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="4" width="4" height="16"/><rect x="15" y="4" width="4" height="16"/></svg>
                      }
                    </button>
                    <span className="ph-video-time" ref={videoTimeRef}>00:00</span>
                    <button
                      className="ph-video-btn ph-video-btn--right"
                      onClick={() => videoRef.current?.requestFullscreen()}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Col droite 25% — Lecteurs audio */}
              <div className="ph-grid-2__audio">
                {AUDIOS.map(({ key, src, label, volume }) => (
                  <AudioPlayer
                    key={key}
                    label={label}
                    src={src}
                    volume={volume}
                  />
                ))}
              </div>

            </div>
          </section>

          {/* ══ PAGE 3 — 2 colonnes 50/50 : OeilSoleil80 | OeilSoleil10 ══ */}
          <section className="ph-section">
            <div className="ph-grid-3">
              <div className="ph-grid-3__col">
                <img src="/media/PHAINO/OeilSoleil61.jpg" alt="Phaínō 80" />
              </div>
              <div className="ph-grid-3__col">
                <img src="/media/PHAINO/OeilSoleil10.jpg" alt="Phaínō 10" />
              </div>
            </div>
          </section>

          {/* ══ PAGE 4 — OeilSoleil20 | OeilSoleil30 (50/50) ══ */}
          <section className="ph-section">
            <div className="ph-grid-4">
              <div className="ph-grid-4__col">
                <img src="/media/PHAINO/OeilSoleil20.jpg" alt="Phaínō 20" />
              </div>
              <div className="ph-grid-4__col">
                <img src="/media/PHAINO/OeilSoleil30.jpg" alt="Phaínō 30" />
              </div>
            </div>
          </section>

          {/* ══ PAGE 5 — screenshot 34% | schéma fond blanc 66% ══ */}
          <section className="ph-section">
            <div className="ph-grid-5">
              <div className="ph-grid-5__screenshot">
                <img
                  src="/media/PHAINO/Screenshot_1.jpg"
                  alt="Capture Phaínō"
                  ref={screenshotRef}
                  onLoad={() => {
                    if (screenshotRef.current)
                      setSchemaH(screenshotRef.current.offsetHeight);
                  }}
                />
              </div>
              <div className="ph-grid-5__schema" style={schemaH ? { height: schemaH } : {}}>
                <img src="/media/PHAINO/Schema2Fichier 1.png" alt="Schéma Phaínō" />
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* ── Puces losange ── */}
      <div className="ph-nav-dots">
        {Array.from({ length: SECTIONS }, (_, i) => (
          <div
            key={i}
            className={`ph-nav-dot${activeSection === i ? ' ph-nav-dot--active' : ''}`}
            onClick={() => navigateTo(i)}
          />
        ))}
      </div>

      {activeSection > 0 && (
        <button className="ph-arrow ph-arrow--left" onClick={() => navigateTo(activeSection - 1)}>‹</button>
      )}
      {activeSection < SECTIONS - 1 && (
        <button className="ph-arrow ph-arrow--right" onClick={() => navigateTo(activeSection + 1)}>›</button>
      )}
    </>
  );
};

export default Phaino;